import { Box, Button, Stack, Typography } from '@mui/material'
import { CollapsibleSection } from 'components/CollapsibleSection'
import { Loading } from 'components/Loading'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSubmissionQuery } from 'services/submissions'
import { ISubmissionEvaluation } from 'types/submissions/submissions-evaluation'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { ProcurementResultSubmission } from './procurement-result-submission'
import { TechnicalResultSubmission } from './technical-result-submission'
import {
  submissionEvaluationApi,
  useSetWinnerSubmissionEvaluationMutation,
} from 'services/submissions/submission-evaluation'
import { tenderApi } from 'services/tender'
import { ETenderStatus, ITender } from 'types/tender'

type Props = {
  sub: ISubmissionEvaluation
  tender: ITender
}

export const ContestResultEvaluationSubmissions = ({ sub, tender }: Props) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<boolean>(false)
  const dispatch = useDispatch()

  const { data: submission, isLoading } = useSubmissionQuery(
    { submissionId: sub.submissionId },
    { skip: !expanded }
  )

  const [
    setSubmissionWinner,
    {
      isError: isTenderWinnerError,
      isLoading: isTenderWinnerLoading,
      isSuccess: isTenderWinnerSuccess,
    },
  ] = useSetWinnerSubmissionEvaluationMutation()

  const handleChangeAccordion = (expanded: boolean) => {
    setExpanded(expanded)
  }

  useEffect(() => {
    if (isTenderWinnerSuccess) {
      toast.success(t('operation-success'))
    }
    if (isTenderWinnerError) {
      toast.error(t('operation-failure'))
    }
  }, [isTenderWinnerError, isTenderWinnerSuccess, t])

  const handleSubmissionWinner = () => {
    setSubmissionWinner({
      id: sub.submissionEvaluationId,
    })
    dispatch(tenderApi.util.invalidateTags(['TenderById']))
    dispatch(submissionEvaluationApi.util.invalidateTags(['SubmissionEvaluations']))
  }

  return (
    <CollapsibleSection
      variant={sub.winner ? 'success' : 'secondary'}
      title={
        sub.submitterOrganizationName
          ? `${sub.submitterOrganizationName} - ${t('final-price')}: ${sub.totalPrice}`
          : ''
      }
      additionalTitle={
        <Box sx={{ display: 'flex', gap: '20px', marginRight: '30px' }}>
          <Typography>
            {t('required-docs')}: {formatTimeStampToDate(sub.submissionCreatedDate)}
          </Typography>
          <Typography>
            {t('tender-proposal')}:{' '}
            {formatTimeStampToDate(sub.submissionCreatedDate)}
          </Typography>
        </Box>
      }
      defaultExpanded={false}
      onChange={(event: React.SyntheticEvent, expanded: boolean) =>
        handleChangeAccordion(expanded)
      }
    >
      {isLoading || isTenderWinnerLoading ? (
        <Loading fullPage={false} />
      ) : (
        <>
          <Stack sx={{ marginTop: '20px' }} spacing={1}>
            {submission?.procurementSubmission.procurementRequirement && (
              <CollapsibleSection
                defaultExpanded={false}
                title={t('price-proposal')}
                additionalTitle={
                  <Box sx={{ marginRight: '30px' }}>
                    <Typography>
                      {t('submission-presentation-date')}:{' '}
                      {formatTimeStampToDate(sub.submissionCreatedDate)}
                    </Typography>
                  </Box>
                }
              >
                <ProcurementResultSubmission
                  procurementRequirement={
                    submission.procurementSubmission.procurementRequirement
                  }
                />
              </CollapsibleSection>
            )}
            {submission?.technicalSubmission.technicalSubmissionItems && (
              <CollapsibleSection
                defaultExpanded={false}
                title={t('technical-proposal')}
                additionalTitle={
                  <Box sx={{ marginRight: '30px' }}>
                    <Typography>
                      {t('submission-presentation-date')}:{' '}
                      {formatTimeStampToDate(sub.submissionCreatedDate)}
                    </Typography>
                  </Box>
                }
              >
                <TechnicalResultSubmission
                  technicalSubmissionItems={
                    submission.technicalSubmission.technicalSubmissionItems
                  }
                />
              </CollapsibleSection>
            )}
          </Stack>
          {tender.status !== ETenderStatus.COMMISSION_EVALUATION && (
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              sx={{ marginTop: '20px' }}
            >
              <Button
                size="small"
                variant="contained"
                style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                form="evaluation-form"
                type="submit"
                onClick={handleSubmissionWinner}
              >
                {t('choose-winner')}
              </Button>
            </Stack>
          )}
        </>
      )}
    </CollapsibleSection>
  )
}
